import { useQuery, type QueryKey, type UseQueryOptions } from "react-query";
import {
  getStatusesList,
  getDropdownKinds,
  getRegionsV2,
  getBUV2,
  getUserGroups,
  getActiveUsers,
  getFileMimeType,
  getExtraJSON
} from "../services/common.services";
import type { UseQueryReturnType } from "@certa/common/types";
import type { FilePreviewDataSource } from "@certa/types";

export const getStatusesListQuery = () => ({
  retry: 0,
  staleTime: Infinity,
  queryKey: "statusesList",
  queryFn: getStatusesList
});

export const useGetStatusesListQuery = () => useQuery(getStatusesListQuery());

export const useDropdownKinds = (kindsToExclude: string[] = []) =>
  useQuery({
    retry: 0,
    staleTime: Infinity,
    queryKey: ["dropdownKinds", kindsToExclude],
    queryFn: () => getDropdownKinds(kindsToExclude)
  });

export const getRegionsV2Query = (
  ...params: Parameters<typeof getRegionsV2>
): UseQueryOptions<UseQueryReturnType<typeof getRegionsV2>> => ({
  retry: 0,
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchInterval: false,
  queryKey: ["regions-list-v2", ...params],
  queryFn: () => getRegionsV2(...params)
});

export const useGetRegionsV2 = (...params: Parameters<typeof getRegionsV2>) =>
  useQuery(getRegionsV2Query(...params));

export const getBUV2Query = (
  ...params: Parameters<typeof getBUV2>
): UseQueryOptions<UseQueryReturnType<typeof getBUV2>> => {
  const payload = JSON.parse(JSON.stringify(params || [{}]));
  return {
    retry: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    queryKey: ["business-unit-list-v2", ...params],
    queryFn: () => getBUV2(payload[0])
  };
};

export const useGetBUV2 = (...params: Parameters<typeof getBUV2>) => {
  return useQuery(getBUV2Query(...params));
};

export const useGetUserGroups = (
  config?: UseQueryOptions<UseQueryReturnType<typeof getUserGroups>>
) =>
  useQuery({
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    ...config,
    queryKey: "user-group-list" as QueryKey,
    queryFn: getUserGroups
  });

export const useGetActiveUsers = (
  config?: UseQueryOptions<UseQueryReturnType<typeof getActiveUsers>>
) =>
  useQuery({
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    ...config,
    queryKey: "active-user-list" as QueryKey,
    queryFn: getActiveUsers
  });

export const useGetFileMimeType = (
  url: string,
  filePreviewDataSource?: FilePreviewDataSource,
  config?: UseQueryOptions<UseQueryReturnType<typeof getFileMimeType>>
) =>
  useQuery({
    ...config,
    queryKey: `file-mime-type-${url}` as QueryKey,
    queryFn: () => getFileMimeType(url, filePreviewDataSource)
  });

export const useGetExtraJSON = (
  url: string,
  config?: UseQueryOptions<UseQueryReturnType<typeof getExtraJSON>>
) =>
  useQuery({
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    ...config,
    queryKey: ["extra-json", url] as QueryKey,
    queryFn: () => getExtraJSON(url)
  });
